import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import { EditActionItemSortOrderDto, FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  actionItemGuid: string;
  sectionGuid: string;
  sortOrder: string;
  planGuid?: string;
};

const useEditActionItemSortOrderRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function editActionItemSortOrderRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: EditActionItemSortOrderDto = {
      sectionGuid: variables.sectionGuid,
      sortOrder: variables.sortOrder,
    };

    const response = await fetch(`${configuration.apiRootUrl}/action-items/${variables.actionItemGuid}/sort-order`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { editActionItemSortOrderRequest };
};

const useEditActionItemSortOrder = () => {
  const { editActionItemSortOrderRequest } = useEditActionItemSortOrderRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editActionItemSortOrderRequest,
    onSettled: async (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.actionItems.summary(variables.actionItemGuid));
      if (variables.planGuid) {
        queryClient.invalidateQueries(queryKeys.plans.getPlan(variables.planGuid));
        queryClient.invalidateQueries(queryKeys.plans.getPlanSubitems(variables.planGuid));
      }
    },
  });
};

export { useEditActionItemSortOrder };
